import React from 'react';

import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { useListRoles } from 'src/features/role/api/useListRoles';
import { useListSites } from 'src/features/sites/api/useListSites';
import SettingsContext from 'src/features/administration/context/SettingsContext';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { siteMessages } from 'src/features/sites/utils/messages';
import {
    DURATION_UNIT_TRANSLATION_MAP,
    MEMBERSHIP_TEMPLATE_TRANSLATION_MAP,
} from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import InputFormField from 'src/registry/components/forms/InputFormField';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import CheckboxFormField from 'src/features/membershipType/components/forms/CheckboxFormField';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';

type MembershipTemplateFormProps = {
    form: UseFormReturn<MembershipTypeFormSchemaValues>;
    onSubmit: (values: MembershipTypeFormSchemaValues) => void;
};

const translationFields = [
    { key: 'name', label: commonMessages.name },
    { key: 'description', label: generalMessages.description },
] as const;

function MembershipTypeForm({ onSubmit, form }: MembershipTemplateFormProps) {
    const { formatMessage, locale } = useIntl();

    const settingsData = React.useContext(SettingsContext);
    const { parkingId } = useParking();

    const { data: sitesData } = useListSites({ parkingId });
    const { data: pricingPlansData } = useListPricingPlans({ parkingId, currently_active: true });
    const { data: rolesData } = useListRoles({ parkingId });

    const { fields } = useFieldArray({
        control: form.control,
        name: 'translations',
    });
    const { translations } = form.getValues();

    React.useEffect(() => {
        if (settingsData?.languages) {
            form.setValue(
                'translations',
                settingsData.languages.map((lang, index) => ({
                    language: lang,
                    name: translations[index]?.name || '',
                })),
            );
        }
    }, [settingsData, form]);

    const filteredEmptySiteTranslations = React.useMemo(() => {
        return sitesData?.results?.filter((site) => site.translations.length > 0) ?? [];
    }, [sitesData?.results]);

    const siteOptions = React.useMemo(
        () =>
            filteredEmptySiteTranslations?.map((site) => {
                const translatedSite = site.translations.find((s) => s.language === locale);

                return { value: site.id, label: translatedSite?.name };
            }),
        [filteredEmptySiteTranslations, locale],
    );

    const filteredEmptyPricingPlanTranslations = React.useMemo(() => {
        return pricingPlansData?.results?.filter((plan) => plan.translations.length > 0) ?? [];
    }, [pricingPlansData?.results]);

    const pricingPlanOptions = React.useMemo(
        () =>
            filteredEmptyPricingPlanTranslations?.map((plan) => {
                const translatedPlan = plan.translations.find((p) => p.language === locale);

                return { value: plan.identity, label: translatedPlan?.name };
            }),
        [filteredEmptyPricingPlanTranslations, locale],
    );

    const {
        limit_duration,
        custom_duration,
        has_pricing_plan,
        has_allowed_spaces_count,
        allow_custom_space_count_definition,
        allow_extra_visitors,
        category,
    } = form.watch();

    React.useEffect(() => {
        if (!limit_duration) {
            form.resetField('duration_unit');
            form.resetField('duration');
            form.resetField('custom_duration');
        }
        if (limit_duration && custom_duration) {
            form.resetField('duration_unit');
            form.resetField('duration');
        }
        if (!has_pricing_plan) {
            form.resetField('apply_pricing_per_space');
            form.resetField('auto_renew');
            form.resetField('pricing_plan_identity');
        }
        if (!has_allowed_spaces_count) {
            form.resetField('allow_custom_space_count_definition');
            form.resetField('allow_extra_visitors');
            form.resetField('max_allowed_spaces_count');
        }
        if (has_allowed_spaces_count && allow_custom_space_count_definition) {
            form.resetField('max_allowed_spaces_count');
        }
        if (!allow_extra_visitors) {
            form.resetField('max_extra_visitors_count');
        }
    }, [
        form,
        limit_duration,
        custom_duration,
        has_pricing_plan,
        has_allowed_spaces_count,
        allow_custom_space_count_definition,
        allow_extra_visitors,
    ]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {translationFields.map(({ key, label }) =>
                fields.map((_, index) =>
                    key === 'name' ? (
                        <InputFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name={`translations.${index}.${key}`}
                            inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                            label={formatMessage(label)}
                        />
                    ) : (
                        <TextareaFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name={`translations.${index}.${key}`}
                            inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                            label={formatMessage(label)}
                        />
                    ),
                ),
            )}
            <SelectFormField<MembershipTypeFormSchemaValues>
                form={form}
                required
                name="category"
                label={formatMessage(commonMessages.type)}
                options={Object.values(MEMBERSHIP_TEMPLATE_CATEGORY)}
                optionTranslationMap={MEMBERSHIP_TEMPLATE_TRANSLATION_MAP}
            />
            <FormField
                control={form.control}
                name="site"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...siteMessages.site} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(siteMessages.site)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {siteOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <Separator />
            <CheckboxFormField form={form} fieldName="has_pricing_plan" title={membershipTypeMessages.hasPricingPlan} />
            <FormField
                control={form.control}
                name="pricing_plan_identity"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required={!!has_pricing_plan}>
                            <FormattedMessage {...pricingPlanMessages.pricingPlan} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={!has_pricing_plan}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(pricingPlanMessages.pricingPlan)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {pricingPlanOptions?.map((option, index) => (
                                            <SelectItem key={`${option?.value}-${index}`} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {category === MEMBERSHIP_TEMPLATE_CATEGORY.SUBSCRIPTION && (
                <>
                    <CheckboxFormField
                        form={form}
                        fieldName="apply_pricing_per_space"
                        title={membershipTypeMessages.applyPricingPerSpace}
                        disabled={!has_pricing_plan}
                    />
                    <CheckboxFormField
                        form={form}
                        fieldName="auto_renew"
                        title={membershipTypeMessages.autoRenew}
                        disabled={!has_pricing_plan}
                    />
                </>
            )}
            <Separator />
            <CheckboxFormField form={form} fieldName="limit_duration" title={membershipTypeMessages.limitDuration} />
            <CheckboxFormField
                form={form}
                fieldName="custom_duration"
                title={membershipTypeMessages.customDuration}
                disabled={!limit_duration}
            />
            <FormItem variant="dialog">
                <FormLabel variant="dialog" required={Boolean(limit_duration) && !custom_duration}>
                    {formatMessage(membershipTypeMessages.membershipDuration)}
                </FormLabel>

                <div className="flex w-full gap-2">
                    <div className="flex-1">
                        <InputFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="duration"
                            type="number"
                            disabled={!limit_duration || (Boolean(limit_duration) && Boolean(custom_duration))}
                            placeholder={`${formatMessage(membershipTypeMessages.duration)}...`}
                        />
                    </div>
                    <div className="flex-1">
                        <FormField
                            control={form.control}
                            name="duration_unit"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormControlStack>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value ?? ''}
                                                disabled={
                                                    !limit_duration ||
                                                    (Boolean(limit_duration) && Boolean(custom_duration))
                                                }
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={`${formatMessage(membershipTypeMessages.durationUnit)}...`}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(DURATION_UNIT_TRANSLATION_MAP)?.map(
                                                        ([key, value]) => (
                                                            <SelectItem key={key} value={key}>
                                                                {formatMessage(value)}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
            </FormItem>
            <Separator />
            <CheckboxFormField
                form={form}
                fieldName="has_time_restriction"
                title={membershipTypeMessages.timeRestriction}
            />
            <CheckboxFormField
                form={form}
                fieldName="has_allowed_spaces_count"
                title={membershipTypeMessages.limitSpaces}
            />
            <CheckboxFormField
                form={form}
                fieldName="allow_custom_space_count_definition"
                title={membershipTypeMessages.customSpaceCount}
                disabled={!has_allowed_spaces_count}
            />
            <InputFormField<MembershipTypeFormSchemaValues>
                form={form}
                name="max_allowed_spaces_count"
                type="number"
                required={!allow_custom_space_count_definition && !!has_allowed_spaces_count}
                disabled={!has_allowed_spaces_count || !!allow_custom_space_count_definition}
                placeholder={`${formatMessage(membershipTypeMessages.maxAllowedSpaces)}...`}
                label={formatMessage(membershipTypeMessages.maxAllowedSpaces)}
            />
            <CheckboxFormField
                form={form}
                fieldName="allow_extra_visitors"
                title={membershipTypeMessages.extraVisitors}
                disabled={!has_allowed_spaces_count}
            />
            <InputFormField<MembershipTypeFormSchemaValues>
                form={form}
                name="max_extra_visitors_count"
                type="number"
                required={!!allow_extra_visitors}
                disabled={!allow_extra_visitors}
                placeholder={`${formatMessage(membershipTypeMessages.maxExtraVisitors)}...`}
                label={formatMessage(membershipTypeMessages.maxExtraVisitors)}
            />
            <Separator />
            <CheckboxFormField
                form={form}
                fieldName="allow_access_over_capacity_limit"
                title={membershipTypeMessages.allowAccessOverCapacityLimit}
            />
            <CheckboxFormField
                form={form}
                fieldName="allow_access_as_client_outside_restrictions"
                title={membershipTypeMessages.allowAccessAsClientOutsideRestrictions}
            />
            <CheckboxFormField
                form={form}
                fieldName="contribute_to_available_spaces"
                title={membershipTypeMessages.contributeToAvailableSpaces}
            />
            <MultiSelectFormField<MembershipTypeFormSchemaValues>
                form={form}
                name="limit_to_roles"
                required={false}
                label={formatMessage(membershipTypeMessages.limitToRoles)}
                options={rolesData?.results}
            />
        </Form>
    );
}

export default MembershipTypeForm;
